/**
 * ACIL Intelligence - Sidebar Navigation
 * Navegación dinámica basada en permisos y módulos activos del cliente.
 */
import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import {
  LayoutDashboard, Users, Clock, TrendingUp, Eye, Flame,
  AlertTriangle, Bell, Building2, UserCheck, Cpu, Radio,
  FileText, Settings, Shield, ScrollText, User, BookOpen,
  ClipboardCheck, Thermometer, Layers, Stethoscope, Calculator, Zap, RadioTower, BrainCircuit, MapPinned,
} from 'lucide-react';

// Mapeo ruta → clave en client_modules (solo módulos operativos controlables)
const MODULE_KEYS = {
  '/aperturas-cierres':      'aperturas_cierres',
  '/control-personal':       'control_personal',
  '/inteligencia-operativa': 'inteligencia_operativa',
  '/seguridad-incidencias':  'seguridad_incidencias',
  '/conteo-personas':        'deteccion_uniforme',
  '/temperatura':            'temperatura',
  '/citas-medicas':          'citas_medicas',
  '/estimaciones-comerciales':'estimaciones_comerciales',
  '/consumo-energia':        'consumo_energia',
  '/torre-control':          'torre_control',
  '/analitica-comportamiento':'analitica_comportamiento',
  '/higiene-cumplimiento':   'higiene_cumplimiento',
};

const OPTIONAL_MODULE_KEYS = new Set(['citas_medicas', 'estimaciones_comerciales', 'consumo_energia', 'torre_control', 'analitica_comportamiento']);

const NAV_SECTIONS = [
  {
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'ver_dashboard' },
    ],
  },
  {
    label: 'Operaciones',
    items: [
      { path: '/torre-control',          label: 'Alertas prioritarias',    icon: AlertTriangle,  permission: 'ver_torre_control' },
      { path: '/aperturas-cierres',      label: 'Aperturas y Cierres',     icon: Clock,          permission: 'ver_apertura_cierre' },
      { path: '/control-personal',       label: 'Control de Personal',     icon: UserCheck,      permission: 'ver_asistencia' },
      { path: '/inteligencia-operativa', label: 'Inteligencia Operativa',  icon: TrendingUp,     permission: 'ver_trafico' },
      { path: '/analitica-comportamiento', label: 'Analítica de Comportamiento', icon: BrainCircuit, permission: 'ver_analitica_comportamiento' },
      { path: '/consumo-energia',        label: 'Consumo de Energía',      icon: Zap,            permission: 'ver_consumo_energia' },
      { path: '/seguridad-incidencias',  label: 'Seguridad e Incidencias', icon: Eye,            permission: 'ver_comportamiento', minLevel: 4 },
      { path: '/conteo-personas',        label: 'Conteo de Personas',      icon: Users,          permission: 'ver_conteo_personas' },
      { path: '/temperatura',            label: 'Temperatura',             icon: Thermometer,    permission: 'ver_temperatura' },
      { path: '/citas-medicas',          label: 'Duración de Estancia',    icon: Clock,          permission: 'ver_duracion_estancia' },
      { path: '/higiene-cumplimiento',   label: 'Higiene y Cumplimiento',  icon: ClipboardCheck, permission: 'ver_higiene_cumplimiento' },
      { path: '/mapas-calor',            label: 'Zonas de Afluencia',      icon: Flame,          permission: 'ver_mapas_calor' },
    ],
  },
  {
    label: 'Comercial',
    items: [
      { path: '/estimaciones-comerciales', label: 'Estimaciones Comerciales', icon: Calculator, permission: 'ver_estimaciones_comerciales' },
    ],
  },
  {
    items: [
      { path: '/incidencias',        label: 'Incidencias',             icon: AlertTriangle, permission: 'ver_incidencias' },
      { path: '/reglas-incidencias', label: 'Monitoreo',               icon: Bell,          minLevel: 4 },
      { path: '/alertas',            label: 'Alertas',                 icon: Bell,          permission: 'ver_alertas' },
    ],
  },
  {
    label: 'Infraestructura',
    items: [
      { path: '/sucursales',   label: 'Sucursales',   icon: Building2, minLevel: 3 },
      { path: '/regiones',      label: 'Regiones',     icon: MapPinned, minLevel: 3 },
      { path: '/empleados',    label: 'Empleados',    icon: Users,     permission: 'ver_empleados' },
      { path: '/dispositivos', label: 'Dispositivos', icon: Cpu,       minLevel: 4 },
      { path: '/fuentes',      label: 'Fuentes',      icon: Radio,     minLevel: 4 },
    ],
  },
  {
    items: [
      { path: '/reportes', label: 'Reportes', icon: FileText, permission: 'ver_reportes' },
    ],
  },
  {
    label: 'Administración',
    items: [
      { path: '/clientes',         label: 'Clientes',               icon: Building2, minLevel: 4 },
      { path: '/cuentas',          label: 'Cuentas',                icon: Layers,    minLevel: 4 },
      { path: '/usuarios',         label: 'Usuarios',               icon: Users,     minLevel: 3 },
      { path: '/logs',             label: 'Registros de auditoría', icon: ScrollText,minLevel: 4 },
      { path: '/configuracion',    label: 'Configuración',          icon: Settings,  minLevel: 4 },
      { path: '/configuracion/notificaciones', label: 'Política de notificaciones', icon: Bell, minLevel: 3 },
      { path: '/catalogo-eventos', label: 'Catálogo de eventos',    icon: BookOpen,  minLevel: 4 },
    ],
  },
  {
    items: [
      { path: '/notificaciones', label: 'Mis notificaciones', icon: Bell, always: true },
      { path: '/perfil', label: 'Mi perfil', icon: User, always: true },
    ],
  },
];

export default function Sidebar({ mobileOpen = false, onClose }) {
  const { user } = useAuth();
  const location = useLocation();

  // Todos los hooks ANTES de cualquier return condicional
  const [openIncidents, setOpenIncidents] = useState(0);
  const [clientModules, setClientModules] = useState(null); // null = aún no cargado

  const perms = user?.permissions || [];
  const level = user?.role?.level || 1;
  const clientId = user?.client_id;

  // Cargar módulos activos del cliente (solo para usuarios no-SA).
  // En vista global multi-cliente, Duración solo se considera habilitada cuando
  // está activa en TODOS los clientes accesibles; así no se mezclan datos de
  // contratos donde el módulo está deshabilitado.
  useEffect(() => {
    if (!user || level >= 4) return;

    const ids = clientId
      ? [clientId]
      : (user?.accessible_clients || []).map(c => c.id).filter(Boolean);
    if (ids.length === 0) {
      setClientModules({});
      return;
    }

    let cancelled = false;
    setClientModules(null);
    (async () => {
      try {
        const results = await Promise.all(
          ids.map(id => api.get(`/api/client-modules/${id}`))
        );
        const moduleSets = results.map(({ data }) => data?.modules || {});

        if (!cancelled) {
          if (clientId) {
            setClientModules(moduleSets[0] || {});
          } else {
            // En vista global, los módulos opcionales solo aparecen si están
            // habilitados en todos los clientes accesibles.
            const optionalModules = {};
            OPTIONAL_MODULE_KEYS.forEach(key => {
              optionalModules[key] = moduleSets.length > 0 &&
                moduleSets.every(mods => mods?.[key] === true);
            });
            setClientModules(optionalModules);
          }
        }
      } catch {
        // Los módulos opcionales permanecen ocultos si no se puede verificar
        // la configuración del cliente.
        if (!cancelled) setClientModules({});
      }
    })();

    return () => { cancelled = true; };
  }, [clientId, level, user]);

  // Badge de alertas sin leer
  useEffect(() => {
    if (!user || level < 2) return;
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/api/alerts/unread-count');
        setOpenIncidents(data?.count || 0);
      } catch { /* silencioso */ }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => clearInterval(timer);
  }, [level, user]);

  if (!user) return null;

  const canSee = (item) => {
    if (item.always) return true;
    // SA (nivel 4) siempre ve todos los módulos sin restricciones
    if (level >= 4) return true;
    if (item.minLevel && level < item.minLevel) return false;
    if (item.permission && !perms.includes(item.permission)) return false;
    // Los módulos opcionales deben estar explícitamente activos.
    // Durante la carga o ante un error permanecen ocultos.
    const moduleKey = MODULE_KEYS[item.path];
    if (OPTIONAL_MODULE_KEYS.has(moduleKey)) {
      return clientModules?.[moduleKey] === true;
    }
    if (clientModules !== null && moduleKey && clientModules[moduleKey] === false) {
      return false;
    }
    return true;
  };

  const initials = (user.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside className={`acil-sidebar ${mobileOpen ? 'mobile-open' : ''}`} data-testid="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <Shield size={20} />
        </div>
        <div>
          <div className="sidebar-logo-text">ACIL Intelligence</div>
          <div className="sidebar-logo-sub">ACIL Mexico</div>
        </div>
      </div>

      {/* Navegación */}
      <nav style={{ flex: 1, paddingBottom: 12 }}>
        {NAV_SECTIONS.map((section, si) => {
          const visible = section.items.filter(canSee);
          if (visible.length === 0) return null;
          return (
            <div className="sidebar-section" key={si}>
              {section.label && (
                <div className="sidebar-section-label">{section.label}</div>
              )}
              {visible.map((item) => {
                const Icon = item.icon;
                // Ítems inactivos: solo Super Admin, sin navegación
                if (item.inactive) {
                  return (
                    <div
                      key={item.path}
                      className="sidebar-item"
                      data-testid={`sidebar-nav-${item.path.replace(/\//g, '-').slice(1)}`}
                      style={{ cursor: 'not-allowed', opacity: 0.55, userSelect: 'none' }}
                    >
                      <Icon className="item-icon" size={20} style={{ color: '#9CA3AF' }} />
                      <span style={{ flex: 1, color: '#9CA3AF' }}>{item.label}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 100,
                        background: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB',
                        letterSpacing: '0.03em', whiteSpace: 'nowrap',
                      }}>SIN DATOS</span>
                    </div>
                  );
                }
                const isActive = location.pathname === item.path ||
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    data-testid={`sidebar-nav-${item.path.replace(/\//g, '-').slice(1) || 'dashboard'}`}
                    onClick={() => onClose?.()}
                  >
                    <Icon className="item-icon" size={20} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.path === '/incidencias' && openIncidents > 0 && level >= 2 && (
                      <span
                        data-testid="sidebar-incidents-badge"
                        style={{
                          fontSize: 10, fontWeight: 700, minWidth: 18, textAlign: 'center',
                          padding: '2px 5px', borderRadius: 100,
                          background: '#DC2626', color: 'white', lineHeight: 1.4,
                        }}
                      >
                        {openIncidents > 99 ? '99+' : openIncidents}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer" data-testid="sidebar-user-info">
        <div className="sidebar-avatar">{initials}</div>
        <div>
          <div className="sidebar-user-name">{user.name}</div>
          <div className="sidebar-user-role">{user.role?.name}</div>
        </div>
      </div>
    </aside>
  );
}
